/* ==========================================================================
   Luxe Glow – theme.js  (vanilla ES6+, no dependencies besides Swiper)
   Modules: utils · dark mode · drawers · header · cart (AJAX) · product cards ·
   carousels · tabs · before/after · reveal · product page · predictive search
   ========================================================================== */
(() => {
  'use strict';
  const T = window.theme || {};
  const S = T.strings || {};
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const on = (ctx, ev, sel, fn) => ctx.addEventListener(ev, e => { const t = e.target.closest(sel); if (t && ctx.contains(t)) fn(e, t); });

  /* ---------- Money ---------- */
  const formatMoney = (cents) => {
    const fmt = T.moneyFormat || '{{amount}}';
    const n = (v, p = 2, th = ',', dec = '.') => {
      const x = (v / 100).toFixed(p).split('.');
      return x[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + th) + (x[1] ? dec + x[1] : '');
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => {
      switch (k) {
        case 'amount': return n(cents);
        case 'amount_no_decimals': return n(cents, 0);
        case 'amount_with_comma_separator': return n(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return n(cents, 0, '.', ',');
        case 'amount_no_decimals_with_space_separator': return n(cents, 0, ' ');
        default: return n(cents);
      }
    });
  };

  const toast = (msg) => {
    const el = $('#LgToast'); if (!el) return;
    el.textContent = msg; el.classList.add('is-show');
    clearTimeout(toast._t); toast._t = setTimeout(() => el.classList.remove('is-show'), 2600);
  };

  /* ---------- Dark mode ---------- */
  const setTheme = (t) => {
    document.body.setAttribute('data-theme', t);
    try { localStorage.setItem('lg-theme', t); } catch (e) {}
    const m = $('meta[name="theme-color"]');
    if (m) m.setAttribute('content', getComputedStyle(document.body).getPropertyValue('--bg-primary').trim());
  };
  on(document, 'click', '[data-theme-toggle]', () => setTheme(document.body.getAttribute('data-theme') === 'dark' ? 'light' : 'dark'));

  /* ---------- Drawers ---------- */
  const overlay = () => $('#LgOverlay');
  const openDrawer = (id) => {
    const d = document.getElementById(id); if (!d) return;
    $$('.drawer.is-open').forEach(x => x !== d && closeDrawer(x.id));
    d.classList.add('is-open'); d.setAttribute('aria-hidden', 'false');
    overlay()?.classList.add('is-open'); document.body.classList.add('no-scroll');
    const f = $('[data-autofocus]', d); if (f) setTimeout(() => f.focus(), 350);
  };
  const closeDrawer = (id) => {
    const d = id ? document.getElementById(id) : $('.drawer.is-open'); if (!d) return;
    d.classList.remove('is-open'); d.setAttribute('aria-hidden', 'true');
    if (!$('.drawer.is-open')) { overlay()?.classList.remove('is-open'); document.body.classList.remove('no-scroll'); }
  };
  on(document, 'click', '[data-drawer-open]', (e, t) => { e.preventDefault(); openDrawer(t.dataset.drawerOpen); });
  on(document, 'click', '[data-drawer-close]', () => closeDrawer());
  on(document, 'click', '#LgOverlay', () => closeDrawer());
  document.addEventListener('keydown', e => { if (e.key === 'Escape') closeDrawer(); });

  /* ---------- Header ---------- */
  const header = $('.header');
  if (header) {
    const f = () => header.classList.toggle('is-scrolled', window.scrollY > 8);
    f(); window.addEventListener('scroll', f, { passive: true });
  }
  on(document, 'change', '[data-locale-select]', (e, t) => { const f = t.closest('form'); if (f) f.submit(); });

  /* ---------- Cart (AJAX) ---------- */
  const Cart = {
    get root() { return $('[data-cart-drawer-content]'); },
    updateCount(n) { $$('[data-cart-count]').forEach(el => { el.textContent = n; el.dataset.count = n; }); },
    render(html) {
      if (!html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = $('[data-cart-drawer-content]', doc);
      if (next && this.root) {
        const keepNote = $('[data-cart-note]', this.root);
        const noteVal = keepNote && document.activeElement === keepNote ? keepNote.value : null;
        this.root.innerHTML = next.innerHTML;
        if (noteVal !== null) $('[data-cart-note]', this.root).value = noteVal;
        this.updateCount(next.dataset.itemCount || 0);
        this.afterRender();
      }
    },
    afterRender() {
      const bar = $('[data-ship-fill]', this.root);
      if (bar) { const p = bar.dataset.progress || 0; requestAnimationFrame(() => requestAnimationFrame(() => { bar.style.width = p + '%'; })); }
      this.loadCrossSell();
    },
    async refresh(open) {
      const r = await fetch(`${T.rootUrl}?sections=cart-drawer`);
      const j = await r.json(); this.render(j['cart-drawer']);
      if (open) openDrawer('CartDrawer');
    },
    async add(items, opts = {}) {
      const r = await fetch(T.cartAddUrl + '.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: 'cart-drawer' })
      });
      let j = {}; try { j = await r.json(); } catch (err) {}
      if (!r.ok) throw new Error(j.description || j.message || 'Error');
      if (j.sections && j.sections['cart-drawer']) this.render(j.sections['cart-drawer']); else await this.refresh(false);
      if (opts.open !== false) openDrawer('CartDrawer');
      return j;
    },
    async change(line, quantity) {
      this.root?.classList.add('is-busy');
      try {
        const r = await fetch(T.cartChangeUrl + '.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line, quantity, sections: 'cart-drawer' })
        });
        const j = await r.json();
        if (!r.ok) throw new Error(j.description || 'Error');
        this.render(j.sections && j.sections['cart-drawer']);
      } catch (err) { toast(err.message); this.refresh(); }
      finally { this.root?.classList.remove('is-busy'); }
    },
    async saveNote(note) {
      await fetch(T.cartUpdateUrl + '.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note }) });
    },
    async loadCrossSell() {
      const box = $('[data-cross-sell]', this.root); if (!box) return;
      const pid = box.dataset.productId; if (!pid) return;
      try {
        const inCart = (box.dataset.inCart || '').split(',');
        const r = await fetch(`/recommendations/products.json?product_id=${pid}&limit=8&intent=complementary`);
        const j = await r.json();
        const list = (j.products || []).filter(p => p.available && !inCart.includes(String(p.id))).slice(0, 3);
        if (!list.length) return;
        const wrap = $('[data-cross-list]', box);
        wrap.innerHTML = list.map(p => {
          const v = p.variants.find(x => x.available) || p.variants[0];
          const img = p.featured_image ? (p.featured_image.startsWith('//') ? 'https:' + p.featured_image : p.featured_image) : '';
          const single = p.variants.length === 1;
          return `<div class="cross-item"><img src="${img}" alt="" width="56" height="56" loading="lazy"><div><p><a href="${p.url}">${p.title}</a></p><strong style="font-size:13px">${formatMoney(v.price)}</strong></div>${single
            ? `<button type="button" class="btn btn--ghost btn--sm" data-cross-add="${v.id}"><span class="btn__label">+ ${S.addToCart}</span><span class="btn__spinner"></span></button>`
            : `<a class="btn btn--ghost btn--sm" href="${p.url}">${'›'}</a>`}</div>`;
        }).join('');
        box.hidden = false;
      } catch (e) { /* keep server-rendered fallback */ }
    }
  };

  on(document, 'click', '[data-cart-qty]', (e, t) => {
    const line = +t.dataset.line; const cur = +t.dataset.qty; const d = +t.dataset.cartQty;
    Cart.change(line, Math.max(0, cur + d));
  });
  on(document, 'change', '[data-cart-qty-input]', (e, t) => Cart.change(+t.dataset.line, Math.max(0, parseInt(t.value, 10) || 0)));
  on(document, 'click', '[data-cart-remove]', (e, t) => { e.preventDefault(); Cart.change(+t.dataset.cartRemove, 0); });
  on(document, 'change', '[data-cart-note]', (e, t) => Cart.saveNote(t.value));
  on(document, 'click', '[data-cross-add]', async (e, t) => {
    t.classList.add('is-loading'); t.disabled = true;
    try { await Cart.add([{ id: +t.dataset.crossAdd, quantity: 1 }], { open: false }); toast(S.added); }
    catch (err) { toast(err.message); }
  });
  on(document, 'click', '[data-promo-apply]', async (e, t) => {
    const input = $('[data-promo-input]'); const msg = $('[data-promo-msg]') || { dataset: {} }; const code = (input?.value || '').trim();
    if (!code) return;
    try { await fetch(`/discount/${encodeURIComponent(code)}`); sessionStorage.setItem('lg-promo', code); msg.textContent = '✓ ' + code + ' – ' + (msg.dataset.ok || ''); }
    catch (err) { msg.textContent = err.message; }
  });
  on(document, 'click', '[data-checkout]', (e, t) => {
    const code = sessionStorage.getItem('lg-promo');
    if (code) { e.preventDefault(); window.location.href = '/checkout?discount=' + encodeURIComponent(code); }
  });
  document.addEventListener('DOMContentLoaded', () => Cart.afterRender());

  /* ---------- Product card: variant switching + quick add ---------- */
  const initCard = (card) => {
    if (card.dataset.ready) return; card.dataset.ready = '1';
    const jsonEl = $('[data-variants]', card);
    let variants = null; try { variants = jsonEl ? JSON.parse(jsonEl.textContent) : null; } catch (err) { variants = null; }
    const form = $('[data-quick-add-form]', card);
    const idInput = $('[data-variant-id]', card);
    const btn = form ? $('button[type=submit]', form) : null;
    const label = btn ? $('[data-label]', btn) : null;

    const state = () => $$('.opt-group', card).map(g => { const a = $('.is-active', g); return a ? a.dataset.value : null; });
    const findVariant = () => { const s = state(); return (variants || []).find(v => v.options.every((o, i) => o === s[i])); };

    const apply = () => {
      if (!variants) return;
      const v = findVariant();
      const priceCur = $('[data-price-current]', card), cmp = $('[data-price-compare]', card), pill = $('[data-badge-sale]', card);
      const stock = $('[data-stock]', card), img = $('[data-main-img]', card);
      if (!v) { if (btn) { btn.disabled = true; if (label) label.textContent = S.unavailable; } return; }
      if (priceCur) priceCur.textContent = v.price;
      const sale = !!v.compare;
      if (cmp) { cmp.hidden = !sale; cmp.textContent = sale ? v.compare : ''; }
      const pw = card.querySelector('.price'); if (pw) pw.classList.toggle('price--sale', sale);
      if (pill) { pill.hidden = !sale; pill.textContent = sale ? `-${v.pct}% OFF` : ''; }
      if (img && v.img) { img.removeAttribute('srcset'); img.src = v.img; }
      if (stock) {
        if (v.available && v.qty !== null && v.qty > 0 && v.qty <= 5) { stock.hidden = false; stock.textContent = S.lowStock.replace('{{ count }}', v.qty); }
        else stock.hidden = true;
      }
      if (idInput) idInput.value = v.id;
      if (btn) { btn.disabled = !v.available; if (label) label.textContent = v.available ? (label.dataset.default || label.textContent) : S.soldOut; }
      // mark unavailable combos for the first-level options
      $$('.opt-group', card).forEach((g, gi) => {
        const cur = state();
        $$('[data-value]', g).forEach(b => {
          const test = cur.slice(); test[gi] = b.dataset.value;
          const m = variants.find(x => x.options.every((o, i) => o === test[i]));
          b.classList.toggle('is-unavailable', !m || !m.available);
        });
      });
    };
    if (label) label.dataset.default = label.textContent;
    on(card, 'click', '[data-value]', (e, t) => {
      const g = t.closest('.opt-group');
      $$('[data-value]', g).forEach(b => { b.classList.remove('is-active'); b.setAttribute('aria-checked', 'false'); });
      t.classList.add('is-active'); t.setAttribute('aria-checked', 'true'); apply();
    });
    if (variants) apply();

    if (form) form.addEventListener('submit', async (e) => {
      e.preventDefault(); if (!btn || btn.disabled) return;
      btn.classList.add('is-loading'); btn.disabled = true;
      try { await Cart.add([{ id: +idInput.value, quantity: 1 }]); }
      catch (err) { toast(err.message); }
      finally { btn.classList.remove('is-loading'); btn.disabled = false; }
    });
  };
  const initCards = (ctx = document) => $$('[data-product-card]', ctx).forEach(initCard);

  /* ---------- Carousels (Swiper) ---------- */
  const initCarousel = (root) => {
    if (root.dataset.ready || typeof Swiper === 'undefined') return; root.dataset.ready = '1';
    const el = $('.swiper', root) || root;
    const d = root.dataset;
    const m = +d.mobile || 2, t = +d.tablet || 3, dt = +d.desktop || 4;
    const cfg = {
      slidesPerView: m, spaceBetween: 12, watchOverflow: true, grabCursor: true,
      loop: d.loop === 'true', speed: 600,
      breakpoints: { 640: { slidesPerView: t, spaceBetween: 18 }, 1024: { slidesPerView: dt, spaceBetween: 22 } }
    };
    if (d.fade === 'true') { cfg.effect = 'fade'; cfg.fadeEffect = { crossFade: true }; cfg.slidesPerView = 1; cfg.breakpoints = {}; }
    if (d.single === 'true') { cfg.slidesPerView = 1; cfg.breakpoints = {}; cfg.spaceBetween = 24; }
    if (+d.autoplay > 0) cfg.autoplay = { delay: +d.autoplay * 1000, disableOnInteraction: false, pauseOnMouseEnter: true };
    const pag = $('.swiper-pagination', root); if (pag) cfg.pagination = { el: pag, clickable: true };
    const scope = root.closest('section') || root;
    const prev = $('[data-prev]', root) || $('[data-prev]', scope), next = $('[data-next]', root) || $('[data-next]', scope);
    if (prev && next) cfg.navigation = { prevEl: prev, nextEl: next };
    root._swiper = new Swiper(el, cfg);
  };
  const initCarousels = (ctx = document) => $$('[data-carousel]', ctx).forEach(initCarousel);

  /* ---------- Tabs ---------- */
  on(document, 'click', '[data-tab]', (e, t) => {
    const wrap = t.closest('[data-tabs]'); const id = t.dataset.tab;
    $$('[data-tab]', wrap).forEach(b => { const a = b === t; b.classList.toggle('is-active', a); b.setAttribute('aria-selected', a); });
    $$('[data-panel]', wrap).forEach(p => {
      const a = p.dataset.panel === id; p.classList.toggle('is-active', a); p.hidden = !a;
      if (a) { initCarousels(p); const c = $('[data-carousel]', p); if (c && c._swiper) c._swiper.update(); }
    });
  });

  /* ---------- Before / After ---------- */
  const initBA = (ba) => {
    if (ba.dataset.ready) return; ba.dataset.ready = '1';
    const r = $('input[type=range]', ba);
    const set = () => ba.style.setProperty('--pos', r.value + '%');
    r.addEventListener('input', set); set();
  };

  /* ---------- Reveal on scroll ---------- */
  const io = 'IntersectionObserver' in window ? new IntersectionObserver((es) => es.forEach(e => { if (e.isIntersecting) { e.target.classList.add('is-visible'); io.unobserve(e.target); } }), { threshold: .25 }) : null;
  const initReveal = (ctx = document) => $$('.editorial', ctx).forEach(el => io ? io.observe(el) : el.classList.add('is-visible'));

  /* ---------- Predictive search ---------- */
  const initSearch = () => {
    const input = $('[data-search-input]'); const out = $('[data-search-results]'); if (!input) return;
    let timer, ctrl;
    input.addEventListener('input', () => {
      clearTimeout(timer); const q = input.value.trim();
      if (q.length < 2) { out.innerHTML = ''; return; }
      timer = setTimeout(async () => {
        ctrl?.abort(); ctrl = new AbortController();
        try {
          const r = await fetch(`${T.searchUrl}.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=last`, { signal: ctrl.signal });
          const j = await r.json(); const list = (j.resources && j.resources.results && j.resources.results.products) || [];
          out.innerHTML = list.length ? list.map(p => `<a href="${p.url}"><img src="${(p.image || '')}" alt="" loading="lazy" width="150" height="150"><p>${p.title}</p><strong>${p.price ? formatMoney(Math.round(parseFloat(p.price) * 100)) : ''}</strong></a>`).join('') : `<p class="empty-state" style="grid-column:1/-1">${S.noResults}</p>`;
        } catch (e) {}
      }, 250);
    });
  };

  /* ---------- Product page ---------- */
  const initProduct = (root) => {
    if (root.dataset.ready) return;
    const form = $('[data-product-form]', root); if (!form) return;
    // variant JSON lives inside the root; fall back to a document lookup so a theme-editor edit can never break the page
    const jsonEl = $('[data-product-json]', root) || $('[data-product-json]');
    let variants = [];
    try { variants = JSON.parse(jsonEl ? jsonEl.textContent : '[]'); } catch (err) { console.error('LuxeGlow: variant JSON error', err); }
    root.dataset.ready = '1';

    const lowAt = +root.dataset.lowStock || 5;
    const idInput = $('[name=id]', form), addBtn = $('[data-add-btn]', form);
    const addLabel = addBtn ? ($('[data-label]', addBtn) || addBtn) : null;
    const stock = $('[data-pdp-stock]', root);
    const sticky = $('[data-sticky-atc]', root) || $('[data-sticky-atc]');
    const qtyInput = $('[name=quantity]', form);

    /* ----- Gallery + thumbnails ----- */
    let main = null, thumbs = null;
    const gal = $('[data-gallery]', root);
    if (gal && typeof Swiper !== 'undefined') {
      const tEl = $('[data-gallery-thumbs]', gal);
      if (tEl) thumbs = new Swiper(tEl, { slidesPerView: 'auto', spaceBetween: 10, watchSlidesProgress: true, freeMode: true, slideToClickedSlide: false });
      main = new Swiper($('[data-gallery-main]', gal), {
        spaceBetween: 0, speed: 500,
        thumbs: thumbs ? { swiper: thumbs } : undefined,
        navigation: { prevEl: $('[data-prev]', gal), nextEl: $('[data-next]', gal) },
        pagination: { el: $('.swiper-pagination', gal), clickable: true }
      });
      // explicit thumbnail triggers (click + keyboard) so the main image always follows
      const goTo = (i) => { if (main && !isNaN(i)) main.slideTo(i); };
      on(gal, 'click', '[data-thumb-index]', (e, t) => goTo(+t.dataset.thumbIndex));
      on(gal, 'keydown', '[data-thumb-index]', (e, t) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goTo(+t.dataset.thumbIndex); } });
      $$('.gallery__slide[data-zoom]', gal).forEach(s => {
        s.addEventListener('mouseenter', () => s.classList.add('is-zoom'));
        s.addEventListener('mouseleave', () => s.classList.remove('is-zoom'));
        s.addEventListener('mousemove', e => { const r = s.getBoundingClientRect(); s.style.setProperty('--zx', ((e.clientX - r.left) / r.width * 100) + '%'); s.style.setProperty('--zy', ((e.clientY - r.top) / r.height * 100) + '%'); });
      });
      main.on('slideChange', () => $$('video', gal).forEach(v => v.pause()));
    }

    /* ----- Variant selection (colour swatches + size pills) ----- */
    const groups = () => $$('[data-option-index]', form);
    const selected = () => groups().map(g => { const c = $('input:checked', g); return c ? c.value : null; });
    const update = () => {
      const s = selected();
      const v = variants.find(x => x.options.every((o, i) => o === s[i]));
      // active highlight + visible selected label
      groups().forEach(g => {
        $$('input', g).forEach(inp => { const lab = $(`label[for="${inp.id}"]`, g); if (lab) lab.classList.toggle('is-active', inp.checked); });
        const c = $('input:checked', g), lbl = $('[data-selected-label]', g); if (c && lbl) lbl.textContent = c.value;
      });
      // dim combos that don't exist / are sold out
      groups().forEach((g, gi) => $$('input', g).forEach(inp => {
        const test = s.slice(); test[gi] = inp.value;
        const m = variants.find(x => x.options.every((o, i) => o === test[i]));
        inp.classList.toggle('is-unavailable', !m || !m.available);
      }));
      const cur = $('[data-price-current]', root), cmp = $('[data-price-compare]', root), pill = $('[data-price-pill]', root), wrap = $('.price', root);
      if (!v) { if (addBtn) { addBtn.disabled = true; addLabel.textContent = S.unavailable; } if (stock) stock.hidden = true; return; }
      if (idInput) idInput.value = v.id;
      const sale = v.compare > v.price;
      if (cur) cur.textContent = v.price_fmt;
      if (cmp) { cmp.hidden = !sale; cmp.textContent = sale ? v.compare_fmt : ''; }
      if (pill) { pill.hidden = !sale; pill.textContent = sale ? `-${Math.round((v.compare - v.price) * 100 / v.compare)}%` : ''; }
      if (wrap) wrap.classList.toggle('price--sale', sale);
      if (addBtn) { addBtn.disabled = !v.available; addLabel.textContent = v.available ? S.addToCart : S.soldOut; }
      if (stock) {
        stock.hidden = false; stock.classList.remove('is-low', 'is-out');
        if (!v.available) { stock.classList.add('is-out'); stock.textContent = S.soldOut; }
        else if (v.qty !== null && v.qty > 0 && v.qty <= lowAt) { stock.classList.add('is-low'); stock.textContent = S.lowStock.replace('{{ count }}', v.qty); }
        else stock.textContent = S.inStock;
      }
      // featured image of the chosen variant
      if (main && v.media_position) main.slideTo(v.media_position - 1);
      else if (!main && v.image) { const im = $('.gallery__slide img', root); if (im) { im.removeAttribute('srcset'); im.src = v.image; } }
      if (sticky) {
        const sp = $('[data-sticky-price]', sticky); if (sp) sp.textContent = v.price_fmt;
        const sb = $('[data-sticky-btn]', sticky);
        if (sb) { sb.disabled = !v.available; const sl = $('[data-label]', sb); if (sl) sl.textContent = v.available ? S.addToCart : S.soldOut; }
        const sv = $('[data-sticky-variant]', sticky); if (sv) sv.textContent = v.title === 'Default Title' ? '' : v.title;
      }
      try { const url = new URL(window.location.href); url.searchParams.set('variant', v.id); window.history.replaceState({}, '', url); } catch (err) {}
    };
    form.addEventListener('change', e => { if (e.target.closest('[data-option-index]')) update(); });
    // labels toggle their (visually hidden) radio; this guarantees an update even if a browser skips 'change'
    on(form, 'click', '[data-option-index] label', () => setTimeout(update, 0));
    update();

    /* ----- Quantity +/- ----- */
    const clampQty = () => { if (!qtyInput) return 1; const n = Math.max(1, parseInt(qtyInput.value, 10) || 1); qtyInput.value = n; return n; };
    on(form, 'click', '[data-qty-step]', (e, t) => { e.preventDefault(); if (qtyInput) qtyInput.value = Math.max(1, clampQty() + (+t.dataset.qtyStep || 0)); });
    if (qtyInput) qtyInput.addEventListener('change', clampQty);

    /* ----- Add to cart (variant id + quantity) ----- */
    const submit = async (btn) => {
      const id = +(idInput && idInput.value); if (!id) { toast(S.unavailable); return; }
      btn.classList.add('is-loading'); btn.disabled = true;
      try { await Cart.add([{ id, quantity: clampQty() }]); }
      catch (err) { toast(err.message); }
      finally { btn.classList.remove('is-loading'); btn.disabled = false; update(); }
    };
    form.addEventListener('submit', e => { e.preventDefault(); if (addBtn && !addBtn.disabled) submit(addBtn); });
    if (sticky) {
      const sb = $('[data-sticky-btn]', sticky);
      if (sb) sb.addEventListener('click', e => { if (!sb.disabled) submit(sb); });
      if ('IntersectionObserver' in window && addBtn) {
        new IntersectionObserver(([en]) => sticky.classList.toggle('is-visible', !en.isIntersecting && en.boundingClientRect.top < 0), { threshold: 0 }).observe(addBtn);
      }
    }
  };

  /* ---------- Announcement bar ---------- */
  const initAnnounce = () => {
    const r = $('[data-announce]'); if (!r || typeof Swiper === 'undefined' || r.dataset.ready) return; r.dataset.ready = '1';
    const n = $$('.swiper-slide', r).length;
    new Swiper($('.swiper', r), { loop: n > 1, allowTouchMove: n > 1, autoplay: n > 1 ? { delay: 4500, disableOnInteraction: false } : false, effect: 'fade', fadeEffect: { crossFade: true } });
  };

  /* ---------- Boot ---------- */
  const boot = (ctx = document) => {
    initCards(ctx); initCarousels(ctx);
    $$('[data-ba]', ctx).forEach(initBA);
    $$('[data-product-root]', ctx).forEach(initProduct);
    initReveal(ctx); initAnnounce();
  };
  const start = () => { boot(); initSearch(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
  window.addEventListener('load', () => boot());

  // Theme editor live reload
  document.addEventListener('shopify:section:load', e => boot(e.target));
  window.LuxeGlow = { Cart, openDrawer, closeDrawer, formatMoney };
})();
